import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { setSignupData, setError, clearInvitationContext } from "../../store/slices/signupSlice";
import { authService } from "../../services/api";

const PasswordStep = () => {
  const dispatch = useDispatch();
  const { email, phoneNumber, error, invitationContext } = useSelector(
    (state) => state.signup
  );
  const [isNotPasswordMatch, setIsNotPasswordMatch] = useState(false);
  const [isNotPasswordEightChar, setISNotPasswordEightChar] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Format phone number to include + prefix
  const formattedPhoneNumber = useMemo(() => {
    if (!phoneNumber) return "";
    return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
  }, [phoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      dispatch(setError("Passwords do not match"));
      setISNotPasswordEightChar(false);
      setIsNotPasswordMatch(true);
      return;
    }

    if (formData.password.length < 8) {
      dispatch(setError("Password must be at least 8 characters"));
      setIsNotPasswordMatch(false);
      setISNotPasswordEightChar(true);
      return;
    }

    setLoading(true);
    dispatch(setError(null));
    setISNotPasswordEightChar(false);
    setIsNotPasswordMatch(false);

    try {
      const registerPayload = {
        email,
        phoneNumber: formattedPhoneNumber,
        password: formData.password,
      };

      // Add invite_token if we have invitation context
      if (invitationContext?.token) {
        registerPayload.invite_token = invitationContext.token;
      }

      const response = await authService.register(registerPayload);

      if (response.success) {
        dispatch(setSignupData({ password: formData.password }));

        // If we have invitation context with token, redirect to home page
        // The backend will handle the invitation processing
        if (invitationContext?.token) {
          if (
            invitationContext.isInvitationFlow &&
            invitationContext.type === "player" &&
            invitationContext.wyscoutId
          ) {
            const playerData = {
              wyscout_id: parseInt(invitationContext.wyscoutId, 10),
              invite_token: invitationContext.token,
            };

            const playerResponse = await authService.createPlayer(playerData);

            if (playerResponse.success) {
              console.log("Player created successfully:", playerResponse.data);
            } else {
              console.error("Failed to create player:", playerResponse.error);
            }
          }
          toast.success(
            "Account created successfully! Welcome to TransferPitch.",
            {
              duration: 5000,
            }
          );
          dispatch(clearInvitationContext());
          navigate("/");
        } else {
          // Normal flow - go to onboarding for profile completion
          navigate("/onboarding");
        }
      } else {
        dispatch(setError(response.error || "Registration failed"));
      }
    } catch (error) {
      console.error("Registration error:", error);
      dispatch(setError("Registration failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-[100px] md:mb-0">
        <h2 className="text-[23.3px] leading-[28px] font-semibold text-[#111927] mb-2">
          Register account
        </h2>
        <p className="text-[14px] leading-[21.98px] text-[#6C737F] mb-8">
          Already have an account?
          <Link
            to="/login"
            className="text-[#2283FF] hover:text-blue-700 ml-1 font-[500]"
          >
            Log in
          </Link>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 flex flex-col gap-2">
        <div>
          <label
            className={`block text-[16px] font-medium ${
              isNotPasswordEightChar || isNotPasswordMatch
                ? "text-[#FF3B30]"
                : "text-black"
            } mb-3`}
          >
            Create new Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className={`placeholder:text-black/60 placeholder:font-medium placeholder:text-[16px] placeholder:leading-[21px] w-full px-4 py-3 border ${
                isNotPasswordEightChar || isNotPasswordMatch
                  ? "border-[#FF3B30]"
                  : "border-gray-300"
              } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0066FF] focus:border-[#0066FF] pr-12`}
              placeholder="New Password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </button>
          </div>
          {!isNotPasswordMatch && !isNotPasswordEightChar && (
            <p className="text-[12px] text-black mt-1">
              Choose a strong password: at least 8 characters, one uppercase
              letter, one number, and one special character for better security.
            </p>
          )}
        </div>

        <div>
          <label
            className={`block text-[16px] font-medium ${
              isNotPasswordEightChar || isNotPasswordMatch
                ? "text-[#FF3B30]"
                : "text-black"
            } mb-3`}
          >
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className={`placeholder:text-black/60 placeholder:font-medium placeholder:text-[16px] placeholder:leading-[21px] w-full px-4 py-3 border ${
                isNotPasswordEightChar || isNotPasswordMatch
                  ? "border-[#FF3B30]"
                  : "border-gray-300"
              } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0066FF] focus:border-[#0066FF] pr-12`}
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </button>
          </div>
          {isNotPasswordEightChar && (
            <p className="text-[12px] text-[#E52F2F] mt-1">
              Password doesn't meet the guidelines. Please use at least 8
              characters, including an uppercase letter, a number, and a special
              character.
            </p>
          )}
          {isNotPasswordMatch && (
            <p className="text-[12px] text-[#E52F2F] mt-1">
              Password doesn't Match. Please ensure both password and confirm
              password are the same.
            </p>
          )}
        </div>

        {error && !isNotPasswordMatch && !isNotPasswordEightChar && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !formData.password || !formData.confirmPassword}
          className={`w-full py-2 px-4 rounded-lg transition-colors ${
            loading || !formData.password || !formData.confirmPassword
              ? "bg-[#2283FF]/50 text-[#F5F5F5] cursor-not-allowed"
              : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
          }`}
        >
          {loading ? "Creating Account..." : "Create Account"}
        </button>
      </form>
    </>
  );
};

export default PasswordStep;
